import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";

import {
	getInspectionValueValidationMessage,
	isValidInspectionValue,
	normalizeInspectionValue,
} from "src/utils/inspection/inspectionValueUtils";
import { showWarning } from "src/utils/toast/toastUtil";

import {
	TQC1060_R01DetailDto,
	TQC1060_R01_VALUE_FIELDS,
} from "../Dto_TQC1060_R01";

const VALUE_FIELDS: (keyof TQC1060_R01DetailDto)[] = [...TQC1060_R01_VALUE_FIELDS];
const EDITABLE_FIELDS: (keyof TQC1060_R01DetailDto)[] = [...VALUE_FIELDS, "remark"];

const validateInspectValue = (row: TQC1060_R01DetailDto, inputValue: string) => {
	if (inputValue === "") {
		return { isValid: true, message: "" };
	}
	return {
		isValid: isValidInspectionValue(row.valueType ?? "", inputValue),
		message: getInspectionValueValidationMessage(row.valueType ?? ""),
	};
};

interface Props {
	updateTQC1060_R01DetailList: (valueIndex: number, data: TQC1060_R01DetailDto) => void;
	originalTQC1060_R01DetailList: TQC1060_R01DetailDto[];
}

export function useTQC1060_R01DetailCellChange({
	updateTQC1060_R01DetailList,
	originalTQC1060_R01DetailList,
}: Props) {
	const handleCellChange = useCallback(
		(event: CellValueChangedEvent<TQC1060_R01DetailDto>) => {
			const { data, colDef, newValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof TQC1060_R01DetailDto;
			const isValueField = VALUE_FIELDS.includes(field);
			const inputValue = normalizeInspectionValue(data.valueType ?? "", newValue);
			const validation = isValueField
				? validateInspectValue(data, inputValue)
				: { isValid: true, message: "" };
			const updated = {
				...data,
				[field]: isValueField
					? validation.isValid
						? inputValue
						: ""
					: newValue,
				isModified: true,
			} as TQC1060_R01DetailDto;
			const original = originalTQC1060_R01DetailList.find(
				(item) => item.valueIndex === data.valueIndex,
			);
			if (original) {
				updated.isModified = !EDITABLE_FIELDS.every(
					(key) => (original[key] ?? "") === (updated[key] ?? ""),
				);
			}
			updateTQC1060_R01DetailList(data.valueIndex, updated);
			if (!validation.isValid && event.node.rowIndex !== null) {
				const rowIndex = event.node.rowIndex;
				showWarning(validation.message);
				setTimeout(() => {
					event.api.ensureIndexVisible(rowIndex);
					event.api.setFocusedCell(rowIndex, colDef.field!);
					event.api.startEditingCell({ rowIndex, colKey: colDef.field! });
				}, 0);
			}
		},
		[originalTQC1060_R01DetailList, updateTQC1060_R01DetailList],
	);
	return { handleCellChange };
}
